/**
 * ServerChatbot.jsx – Floating Groq RAG AI chatbot panel
 */
import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiSend, FiMessageSquare } from 'react-icons/fi';
import { askChatbot } from './serverApi';

const INITIAL_MSG = [
  { sender: 'assistant', text: "Hello! I'm your AI Server Assistant. Ask me anything about customers, servers, storage, backup, monitoring, or analytics!" }
];

export default function ServerChatbot() {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState(INITIAL_MSG);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const bottomRef               = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { sender: 'user', text: q }]);
    setInput('');
    setLoading(true);
    try {
      const filteredHistory = messages.filter(m => m.sender === 'user' || (m.sender === 'assistant' && !m.text?.includes('Welcome')));
      const historyToSend = JSON.stringify(filteredHistory);
      const res = await askChatbot(q, historyToSend);
      if (res.data?.status && res.data.data) {
        setMessages((m) => [...m, { sender: 'assistant', html: res.data.data }]);
      } else {
        setMessages((m) => [...m, { sender: 'assistant', text: res.data?.message || 'No response from AI.' }]);
      }
    } catch (err) {
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        setMessages((m) => [...m, { sender: 'assistant', text: 'Request timed out. Please try again.' }]);
      } else {
        setMessages((m) => [...m, { sender: 'assistant', text: 'Failed to retrieve response from AI assistant. Please check connection and try again.' }]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[150] flex flex-col items-end gap-2">
      {/* Chat window */}
      {open && (
        <div className="w-80 sm:w-96 h-[460px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-200">
          {/* Header */}
          <div className="bg-red-700 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <div>
                <p className="font-bold text-sm leading-none">eNova AI Assistant</p>
                <p className="text-[10px] text-red-200 mt-0.5">Gemeni AI Assistant · Live Server Context</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-red-800 p-1 rounded-full transition">
              <FiX className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-neutral-50 text-sm">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[82%] px-3 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed
                  ${msg.sender === 'user'
                    ? 'bg-red-700 text-white rounded-tr-none'
                    : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none'}`}
                >
                  {msg.html
                    ? <div dangerouslySetInnerHTML={{ __html: msg.html }} className="prose prose-sm max-w-none" />
                    : msg.text
                  }
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-none px-3 py-2.5 shadow-sm flex items-center gap-1.5">
                  {[0, 150, 300].map((d) => (
                    <span key={d} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <form onSubmit={send} className="flex gap-2 p-3 bg-white border-t border-gray-100 flex-shrink-0">
            <input
              type="text" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about CPU, IPs, services…"
              className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
            />
            <button type="submit" disabled={loading}
              className="p-2.5 bg-red-700 hover:bg-red-800 text-white rounded-xl transition flex items-center justify-center">
              <FiSend className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* FAB Toggle */}
      <button
        onClick={() => setOpen((o) => !o)}
        title="AI Server Assistant"
        className="w-14 h-14 bg-red-700 hover:bg-red-800 text-white rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-105"
      >
        <FiMessageSquare className="w-6 h-6" />
      </button>
    </div>
  );
}
